import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, BadgeCheckIcon, CalendarDaysIcon, CircleAlertIcon, ClipboardCheckIcon, MegaphoneIcon } from 'lucide-react';
import { dashboardImages } from '../../data/dashboardVisual';
export type HeroStat = {
  id: 'classes' | 'tasks' | 'overdue' | 'announcements';
  value: number;
  label: string;
  to: string;
};
const STAT_ICONS = {
  classes: CalendarDaysIcon,
  tasks: ClipboardCheckIcon,
  overdue: CircleAlertIcon,
  announcements: MegaphoneIcon
} as const;

/** Only overdue earns an alarm colour. Everything else stays neutral. */
const STAT_TONES: Record<HeroStat['id'], string> = {
  classes: 'bg-brand-50 text-brand-700',
  tasks: 'bg-ok-50 text-ok-700',
  overdue: 'bg-danger-50 text-danger-700',
  announcements: 'bg-info-50 text-info-700'
};
type Props = {
  greeting: string;
  firstName: string;
  programme: string;
  level: string;
  institutionName: string;
  campusName: string;
  status: string;
  stats: HeroStat[];
};

/**
 * The dashboard's anchor. It answers who you are, where you are and what
 * standing you hold, then sets the day's four numbers beside it — each of them
 * a link, because a number you cannot act on is decoration.
 */
export function WelcomeHero({
  greeting,
  firstName,
  programme,
  level,
  institutionName,
  campusName,
  status,
  stats
}: Props) {
  const reduceMotion = useReducedMotion();
  return <section className="grid gap-4 lg:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]" data-id="element-1080" data-mp="30jzqb880s4mz">
      <motion.div initial={reduceMotion ? false : {
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.28,
      ease: [0.23, 1, 0.32, 1]
    }} className="relative overflow-hidden rounded-panel bg-[#0e7c66]" data-id="element-1081" data-mp="30hws40wpr576">
        {/* Illustration sits on the right; the left stays clear for the copy. */}
        <img src={dashboardImages.heroStudent} alt="" className="pointer-events-none absolute inset-y-0 right-0 hidden h-full w-[54%] object-cover object-left sm:block" data-id="element-1082" data-mp="2gws9z4vbgp69" />
        <span aria-hidden className="absolute inset-0 hidden bg-gradient-to-r from-[#0e7c66] via-[#0e7c66]/85 to-transparent sm:block" data-id="element-1083" data-mp="pqx8d5zicmkl" />

        <div className="relative max-w-[30rem] px-6 py-7 sm:px-8 sm:py-9" data-id="element-1084" data-mp="zhif5gpc4xel">
          <h1 className="text-h2 font-bold tracking-tight text-white lg:text-h1" data-id="element-1085" data-mp="zjl1nqra48ek">
            {greeting}, {firstName}
          </h1>
          <p className="mt-3 text-body-lg font-medium text-brand-50" data-id="element-1086" data-mp="6skx9ep1d2md">
            {programme} · {level}
          </p>
          <p className="mt-1 text-body text-brand-100" data-id="element-1087" data-mp="br4itdzpwce7">
            {institutionName} · {campusName}
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-2" data-id="element-1088" data-mp="fvcank1h28gn">
            <Link to="/identity" className="inline-flex min-h-touch items-center gap-2 rounded-full bg-white/15 px-3.5 text-body-sm font-semibold text-white transition-colors duration-150 ease-out hover:bg-white/25" data-id="element-1089" data-mp="1n7vzsm5lbtz5">
              <BadgeCheckIcon aria-hidden className="h-4 w-4" data-id="element-1090" data-mp="3bt35ggjqy0a2" />
              Verified student
            </Link>
            <span className="inline-flex min-h-touch items-center rounded-full border border-white/30 px-3.5 text-body-sm font-medium text-brand-50" data-id="element-1091" data-mp="32yoq6dz7upqy">
              {status}
            </span>
          </div>
        </div>
      </motion.div>

      {/* Four numbers for the day. Two by two so none of them wins by default. */}
      <div className="flex flex-col gap-3" data-id="element-1092" data-mp="2o0wbfo1hu5dt">
        <ul className="grid grid-cols-2 gap-3" data-id="element-1093" data-mp="2o03009rlarpc">
          {stats.map(stat => {
          const Icon = STAT_ICONS[stat.id];
          return <li key={stat.id} data-id="element-1094" data-mp="2f196is9k2hg0">
                <Link to={stat.to} className="group flex h-full flex-col justify-between rounded-card border border-line bg-surface px-4 py-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-[transform,box-shadow,border-color] duration-200 ease-out hover:-translate-y-0.5 hover:border-line-strong hover:shadow-[0_8px_24px_rgba(15,23,42,0.07)]" data-id="element-1095" data-mp="2f2al36ps5m2h">
                  <span className="flex items-start justify-between gap-2" data-id="element-1096" data-mp="3b2ed5ywpx1vt">
                    <span className="cz-num text-h2 font-bold leading-none text-ink" data-id="element-1097" data-mp="2x23kcqbuld80">
                      {stat.value}
                    </span>
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] ${STAT_TONES[stat.id]}`} data-id="element-1098" data-mp="324cdbmoq0fa4">
                      <Icon aria-hidden className="h-5 w-5" data-id="element-1099" data-mp="2s5mxdzgdx4wi" />
                    </span>
                  </span>
                  <span className="mt-3 block text-caption font-medium text-ink-muted" data-id="element-1100" data-mp="2eei4dg7ivav5">
                    {stat.label}
                  </span>
                </Link>
              </li>;
        })}
        </ul>

        <Link to="/notifications" className="group flex min-h-touch-lg items-center justify-center gap-2 rounded-card border border-line bg-surface text-body-sm font-semibold text-ink transition-colors duration-150 ease-out hover:border-line-strong" data-id="element-1101" data-mp="29dkoja9ziswt">
          View all updates
          <ArrowRightIcon aria-hidden className="h-4 w-4 text-ink-muted transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-1102" data-mp="1zpyhltej0thi" />
        </Link>
      </div>
    </section>;
}